import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Chip,
  IconButton,
  Alert,
  Modal,
  Backdrop,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { messages } from "../../../locales/messages";
import { GfImage } from "@swagger/typescript-fetch-goflyer";
import { ImageDropZone } from "../../../components/ImageDropZone";
import { useUploadOrPickImages } from "../../../hooks/useUploadOrPickImages";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import PhotoIcon from "@mui/icons-material/Photo";
import CloseIcon from "@mui/icons-material/Close";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import {
  SectionCard,
  SectionHeader,
  SectionIcon,
  ImageUploadContainer,
  UploadButton,
  ImagePreview,
  PRIMARY_COLOR,
  NEUTRAL_800,
  NEUTRAL_900,
} from "../../pages/FIgmaTemplateData/Create-Edit-Page/styles";

interface PreviewImageSectionProps {
  image: GfImage | undefined;
  onImageChange: (image: GfImage | undefined) => void;
  error?: string;
  onUploadingChange?: (uploading: boolean) => void;
}

const PreviewImageSection: React.FC<PreviewImageSectionProps> = ({
  image,
  onImageChange,
  error,
  onUploadingChange,
}) => {
  const { t } = useTranslation();
  const [imageModalOpen, setImageModalOpen] = useState(false);

  const {
    isUploadingImage,
    handleOnImageFileSelect,
    setPickedGfImages,
    isDragging,
    getDropZoneProps,
    showReorderModal,
    pendingDropFiles,
    handleReorderConfirm,
    handleReorderCancel,
  } = useUploadOrPickImages({
    onPickedImageChange: (images: GfImage[]) => {
      const latest = images[images.length - 1];
      if (latest) {
        onImageChange(latest);
        setPickedGfImages([latest]);
      }
    },
    initialPickedImages: image ? [image] : [],
    dragAndDrop: {
      enabled: true,
      defaultPath: "flyers",
    },
  });

  const imagePreview = image?.src;

  React.useEffect(() => {
    onUploadingChange?.(isUploadingImage);
  }, [isUploadingImage]);

  const handleLocalImageUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    if (e.target.files && e.target.files.length > 0) {
      await handleOnImageFileSelect(e, "flyers");
      e.target.value = "";
    }
  };

  return (
    <>
      <SectionCard data-testid="preview-image-section">
        <SectionHeader>
          <SectionIcon>
            <PhotoIcon />
          </SectionIcon>
          <Box>
            <Typography variant="h6" fontWeight="600" color={NEUTRAL_900}>
              {t(messages.Preview_Image())}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t(messages.Upload_an_image_that_represents_your_template())}
            </Typography>
          </Box>
          <Chip
            label={t(messages.Optional())}
            size="small"
            color="info"
            variant="outlined"
            sx={{ ml: "auto" }}
          />
        </SectionHeader>

        <ImageDropZone
          uploader={{ getDropZoneProps, isDragging, showReorderModal, pendingDropFiles, handleReorderConfirm, handleReorderCancel }}
          label={t(messages.Drag_your_image_here())}
          overlayContent={" "}
          showOverlay={true}
          aria-label={t(messages.Template_Preview_Image_Upload())}
          style={{
            position: "relative",
            width: "100%",
            minHeight: "200px",
          }}
          data-testid="preview-image-section-drop-zone"
        >
          <ImageUploadContainer>
            {imagePreview ? (
              <Stack alignItems="center" spacing={2}>
                <Box
                  sx={{
                    position: "relative",
                    cursor: "pointer",
                    "&:hover": {
                      transform: "scale(1.02)",
                      transition: "transform 0.2s ease-in-out",
                    },
                  }}
                  onClick={() => setImageModalOpen(true)}
                >
                  <ImagePreview
                    src={imagePreview}
                    alt={t(messages.Template_Preview())}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "rgba(0, 0, 0, 0.6)",
                      borderRadius: "50%",
                      padding: "4px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <ZoomInIcon
                      sx={{
                        color: "white",
                        fontSize: 16,
                      }}
                    />
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  startIcon={<UploadFileIcon />}
                  component="label"
                  disabled={isUploadingImage}
                  data-testid="preview-image-section-change-button"
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  {isUploadingImage
                    ? t(messages.Uploading())
                    : t(messages.Change_Image())}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLocalImageUpload}
                    data-testid="preview-image-section-change-input"
                  />
                </Button>
              </Stack>
            ) : (
              <>
                <PhotoIcon
                  sx={{
                    fontSize: 48,
                    color: `${PRIMARY_COLOR}60`,
                    mb: 1,
                  }}
                />
                <Typography
                  variant="h6"
                  fontWeight="600"
                  color={NEUTRAL_800}
                  textAlign="center"
                >
                  {t(messages.Drop_Your_Image_Here())}
                </Typography>
                <UploadButton
                  variant="contained"
                  startIcon={<UploadFileIcon />}
                  component="label"
                  disabled={isUploadingImage}
                  data-testid="preview-image-section-select-button"
                >
                  {isUploadingImage ? (
                    <>
                      <CircularProgress
                        size={16}
                        color="inherit"
                        sx={{ mr: 1 }}
                        data-testid="preview-image-section-loader"
                      />
                      {t(messages.Uploading())}
                    </>
                  ) : (
                    t(messages.Select_Image())
                  )}
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleLocalImageUpload}
                    data-testid="preview-image-section-select-input"
                  />
                </UploadButton>
              </>
            )}
            {error && (
              <Alert
                severity="error"
                sx={{ mt: 2, borderRadius: "8px" }}
                data-testid="preview-image-section-error-alert"
              >
                {error}
              </Alert>
            )}
          </ImageUploadContainer>
        </ImageDropZone>
      </SectionCard>

      <Modal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        BackdropComponent={Backdrop}
        BackdropProps={{
          sx: {
            backgroundColor: "rgba(0, 0, 0, 0.8)",
            backdropFilter: "blur(4px)",
          },
        }}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
        data-testid="preview-image-section-modal"
      >
        <Box
          sx={{
            position: "relative",
            maxWidth: "90vw",
            maxHeight: "90vh",
            backgroundColor: "white",
            borderRadius: "12px",
            boxShadow: "0 20px 40px rgba(0, 0, 0, 0.3)",
            overflow: "hidden",
            outline: "none",
          }}
        >
          <IconButton
            onClick={() => setImageModalOpen(false)}
            data-testid="preview-image-section-modal-close-button"
            sx={{
              position: "absolute",
              top: 12,
              right: 12,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              color: NEUTRAL_800,
              zIndex: 1,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 1)",
              },
            }}
          >
            <CloseIcon />
          </IconButton>

          {imagePreview && (
            <img
              src={imagePreview}
              alt={t(messages.Template_Preview_Enlarged())}
              style={{
                width: "100%",
                height: "auto",
                maxWidth: "100%",
                maxHeight: "90vh",
                objectFit: "contain",
                display: "block",
              }}
            />
          )}
        </Box>
      </Modal>
    </>
  );
};

export default PreviewImageSection;
